/**
 * DaadEntryProfile — top-of-Section-2 callout for German (DAAD-sourced)
 * bachelor programmes. Surfaces the two things international applicants
 * scan first that HESA-style curriculum cards can't answer:
 *
 *   1. Language of instruction + required CEFR levels (English/German).
 *   2. Intl-student support the university offers (welcome week, buddy
 *      programme, visa support, tutors, etc — DAAD ships this per course).
 *
 * Renders nothing when the degree lacks a `daad_stats` subdoc.
 */
import { Card } from "@/components/ui/card";
import { Languages, Globe2 } from "lucide-react";

export function DaadEntryProfile({ degree }) {
  const ds = degree?.daad_stats;
  if (!ds) return null;

  const languages = Array.isArray(ds.languages) ? ds.languages : [];
  const eng = ds.language_level_english;
  const ger = ds.language_level_german;
  const intlSupport = Array.isArray(ds.support_international_students)
    ? ds.support_international_students
    : [];
  const online = ds.is_online_possible === true;

  const hasContent =
    languages.length > 0 ||
    eng ||
    ger ||
    intlSupport.length > 0 ||
    online;
  if (!hasContent) return null;

  return (
    <Card
      className="p-4 mb-5 border-amber-300 bg-amber-50/60"
      data-testid="daad-entry-profile"
    >
      <div className="flex items-center gap-2 mb-3">
        <Languages className="h-4 w-4 text-amber-800" />
        <div className="font-underground-sc text-[10px] tracking-wider text-amber-900">
          Language & delivery (DAAD)
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-stone-800">
        {languages.length > 0 && (
          <div>
            <b>Taught in:</b> {languages.join(" + ")}
          </div>
        )}
        {eng && (
          <div>
            <b>English required:</b> {eng}
          </div>
        )}
        {ger && (
          <div>
            <b>German required:</b> {ger}
          </div>
        )}
        {online && (
          <div className="flex items-center gap-1.5">
            <Globe2 className="h-3.5 w-3.5 text-amber-800" />
            <span>Complete online option available</span>
          </div>
        )}
      </div>

      {intlSupport.length > 0 && (
        <div className="mt-3">
          <div className="font-underground-sc text-[10px] tracking-wider text-amber-900 mb-1.5">
            International-student support
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="daad-intl-support-pills">
            {intlSupport.slice(0, 12).map((s, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-[11px] text-amber-900"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 text-[10px] font-mono text-stone-500">
        Source: DAAD International Programmes · verified{" "}
        {degree?.source_provenance?.date_checked || ""}
      </div>
    </Card>
  );
}

export default DaadEntryProfile;
